import type { SubCustomerAudience, SubCustomerView } from '../types'

export const SUB_CUSTOMER_DASHBOARD_ENDPOINTS = {
  TOP: '/api/sales-dashboard/sub-customers/top',
  DETAIL: '/api/sales-dashboard/sub-customers/detail',
  OPTIONS: '/api/sales-dashboard/sub-customers/options',
  EXPORT: '/api/sales-dashboard/sub-customers/export',
  SEARCH: '/api/sub-customers',
} as const

export const SUB_CUSTOMER_AUDIENCE = {
  ADMIN: 'admin',
  CUSTOMER: 'customer',
} as const satisfies Record<string, SubCustomerAudience>

export const SUB_CUSTOMER_VIEW = {
  CHART: 'chart',
  TABLE: 'table',
} as const satisfies Record<string, SubCustomerView>

/** Sent when the header location has no plant — the server then matches nothing (same as Sales). */
export const NO_MATCHING_FACTORY_ID = '__no_matching_factory__'
export const SUB_CUSTOMER_REFRESH_DEBOUNCE_MS = 300
export const SUB_CUSTOMER_SEARCH_MIN_CHARS = 2
export const SUB_CUSTOMER_SEARCH_PAGE_SIZE = 20
/** Mirrors the backend guard (controllers/sales-dashboard.ts). */
export const SUB_CUSTOMER_MAX_RANGE_DAYS = 366
export const SUB_CUSTOMER_EXPORT_LIMIT = 50_000
/** Donut slices before the tail is folded into "Other". */
export const SUB_CUSTOMER_DONUT_SLICES = 5
/** Portal tokens are oklch, so they are used as-is (hsl(var(--chart-1)) would be invalid). */
export const SUB_CUSTOMER_CHART_COLORS = [
  { fill: 'var(--chart-1)', dot: 'bg-chart-1' },
  { fill: 'var(--chart-2)', dot: 'bg-chart-2' },
  { fill: 'var(--chart-3)', dot: 'bg-chart-3' },
  { fill: 'var(--chart-4)', dot: 'bg-chart-4' },
  { fill: 'var(--chart-5)', dot: 'bg-chart-5' },
  { fill: 'var(--muted-foreground)', dot: 'bg-muted-foreground' },
] as const

/** Page filters a user can set (the plant comes from the header only). */
export const SUB_CUSTOMER_FILTER_GROUPS = ['customerIds', 'cityIds', 'variantIds'] as const

const K = 'salesDashboard.subCustomers'

export const SUB_CUSTOMER_T_KEYS = {
  MAIN_CUSTOMER: `${K}.mainCustomer`,
  PLANT: `${K}.plant`,
  CITY: `${K}.city`,
  CEMENT_TYPE: `${K}.cementType`,
  ORDERS: `${K}.orders`,
  TONS: `${K}.tons`,
  /** Unit after a number ("tons" / "طن"), shared with the Sales dashboard. */
  TONS_UNIT: 'salesDashboard.stats.tons',
  ORDERS_UNIT: `${K}.ordersUnit`,
  SHARE: `${K}.share`,
  RANK: `${K}.rank`,
  DATE: `${K}.date`,
  UNKNOWN_CUSTOMER: `${K}.unknownCustomer`,
  UNKNOWN: `${K}.unknown`,
  OTHER: `${K}.other`,
  NO_DATA: `${K}.noData`,
  LOCKED_HINT: `${K}.lockedHint`,
  RANGE_TOO_LONG: `${K}.rangeTooLong`,
  VIEW: { LABEL: `${K}.view.label`, CHART: `${K}.view.chart`, TABLE: `${K}.view.table` },
  KPI: {
    ORDERS: `${K}.kpi.orders`,
    AVG_TONS_PER_ORDER: `${K}.kpi.avgTonsPerOrder`,
  },
  CHARTS: {
    TREND: `${K}.charts.trend`,
    BY_CEMENT_TYPE: `${K}.charts.byCementType`,
    BY_CITY: `${K}.charts.byCity`,
    BY_PLANT: `${K}.charts.byPlant`,
  },
  FILTERS: {
    BUTTON: `${K}.filters.button`,
    TITLE: `${K}.filters.title`,
    DATE_RANGE: `${K}.filters.dateRange`,
    CLEAR_ALL: `${K}.filters.clearAll`,
    DONE: `${K}.filters.done`,
    /** Existing Sales dashboard search strings, reused as-is. */
    SEARCH_CUSTOMER: 'salesDashboard.filters.searchCustomer',
    SEARCH_CITY: 'salesDashboard.filters.searchCity',
    SEARCH_CEMENT_TYPE: 'salesDashboard.filters.searchProduct',
  },
  SEARCH: { NO_RESULTS: `${K}.search.noResults`, MIN_CHARS: `${K}.search.minChars`, SEARCHING: `${K}.search.searching` },
  DETAIL: {
    CITIES: `${K}.detail.cities`,
    PLANTS: `${K}.detail.plants`,
    CEMENT_TYPES: `${K}.detail.cementTypes`,
  },
  EXPORT: {
    BUTTON: `${K}.export.button`,
    TITLE: `${K}.export.title`,
    DATE_LABEL: `${K}.export.dateLabel`,
    DOWNLOAD: `${K}.export.download`,
    CANCEL: `${K}.export.cancel`,
    ONLY: `${K}.export.only`,
    EMPTY: `${K}.export.empty`,
    NARROW_RANGE: `${K}.export.narrowRange`,
    FAILED: `${K}.export.failed`,
    SHEET: `${K}.export.sheet`,
    COL_ORDER_NUMBER: `${K}.export.colOrderNumber`,
    COL_DELIVERY_DAY: `${K}.export.colDeliveryDay`,
  },
  ERROR: { TITLE: `${K}.error.title`, RETRY: `${K}.error.retry` },
} as const

/** Audience-specific wording keys, relative to the audience block (`${K}.admin.*` / `${K}.customer.*`). */
export const SUB_CUSTOMER_WORDING = {
  TITLE: 'title',
  SUBTITLE: 'subtitle',
  ENTITY: 'entity',
  CODE: 'code',
  ACTIVE: 'active',
  TOTAL: 'total',
  TOP_TITLE: 'topTitle',
  SEARCH_PLACEHOLDER: 'searchPlaceholder',
  NO_SUB_CUSTOMER: 'noSubCustomer',
  EXPORT_THIS: 'exportThis',
  EXPORT_DESCRIPTION: 'exportDescription',
} as const

export type SubCustomerWordingKey = (typeof SUB_CUSTOMER_WORDING)[keyof typeof SUB_CUSTOMER_WORDING]

export interface SubCustomerAudienceConfig {
  /** i18n block holding the audience wording ("Sub-customer" vs the customer portal's "Customer"). */
  wordingPrefix: string
  /** The main customer is the viewer themself in the customer portal. */
  showMainCustomer: boolean
  exportFilePrefix: string
}

// Strategy map: everything that differs per portal lives here, not in `if (audience === …)` branches.
export const AUDIENCE_CONFIG: Record<SubCustomerAudience, SubCustomerAudienceConfig> = {
  admin: { wordingPrefix: `${K}.admin`, showMainCustomer: true, exportFilePrefix: 'sub-customer-orders' },
  customer: { wordingPrefix: `${K}.customer`, showMainCustomer: false, exportFilePrefix: 'customer-orders' },
}

export function wordingKey(audience: SubCustomerAudience, key: SubCustomerWordingKey): string {
  return `${AUDIENCE_CONFIG[audience].wordingPrefix}.${key}`
}
